import { create } from 'zustand'

/**
 * SceneState — the visual/interaction state of the HomeScene.
 *
 * Drives the choreography defined in `docs/flow.md` (state diagram):
 *   idle              → resting scene, character central, zones tinted by recs
 *   hovering-house    → avatar walks to house, mimes claim inspection
 *   house-clicked     → click registered; transitioning to sun-up
 *   sun-up            → sun has risen, sky brightened
 *   qr-shown          → QR overlay visible with CTA "Naskenuj smlouvu"
 *   recs-revealed     → returned from upload+confirm; recommendation stack animating in
 *
 * NOTE — server state (user, currentContract, family, contracts, recommendations)
 * is intentionally NOT in this store. That data is owned by TanStack Query hooks
 * (`useFamily`, `useContracts`, `useRecommendations`, …) which the Mock Data
 * Designer agent ships in parallel. Keeping local UI state and server state in
 * separate layers avoids cache-invalidation collisions.
 *
 * Phase 2.8 (state persistence + modal upload) additions:
 *   - `car-extinguishing` replaces the old click-to-burn semantics. Burning is
 *     now the *default* visual when no vehicle contract exists; clicking the
 *     car triggers the water-pour rescue sequence which fades the flame out.
 *   - `scanning-house | scanning-vehicle | scanning-travel` cover the
 *     "modal-is-open" period where the character poses with a contract paper
 *     and a tiny camera flash pulses.
 */
export type SceneState =
  | 'idle'
  | 'hovering-house'
  | 'hovering-vehicle'
  | 'hovering-person'
  | 'house-clicked'
  | 'sun-up'
  | 'qr-shown'
  | 'recs-revealed'
  // 2.5D pixel-art scene extensions (Phase 2.7) — interactive object choreography.
  | 'car-burning'        // retained alias — the persistent "no contract" flame loop
  | 'car-extinguishing'  // car click → walk over + water pour, then modal opens
  | 'suitcases-grabbed'  // character lerps to suitcases, swaps to "holding" pose
  | 'plane-departing'    // plane slides in, "carries" character off-screen, then modal
  | 'avatar-waving'      // logged-in user clicked the character — wave-pose for ~1.5s
  // 2.8 — scanning poses while the upload modal is open.
  | 'scanning-house'
  | 'scanning-vehicle'
  | 'scanning-travel'
  | 'scanning-life'
  | 'scanning-pet'
  // Life zone (Reaper / Angel) — avatar walks to the reaper, gets cut down,
  // briefly lies on the ground, then the modal opens.
  | 'avatar-walking-to-reaper'
  | 'reaper-swinging'
  | 'avatar-fallen'

/** Which zone (if any) currently has the upload modal open. */
export type ModalZone = 'house' | 'vehicle' | 'travel' | 'life' | 'pet' | null

export type SessionState = {
  sceneState: SceneState
  setSceneState: (s: SceneState) => void
  /**
   * The zone whose upload modal is currently mounted, or `null` for closed.
   * Kept here (not in router state) because the modal renders on top of the
   * scene rather than replacing the route — the home scene must keep
   * rendering behind the backdrop.
   */
  modalZone: ModalZone
  openModal: (z: Exclude<ModalZone, null>) => void
  closeModal: () => void
  /**
   * Phase 2.9 — whether the in-scene `<LoginModal>` is currently mounted.
   * Lives here (rather than in the route) so the avatar click in HomeScene
   * can stay on `/` and just toggle the overlay, same pattern as `modalZone`.
   * The `/login` route still works for direct-URL access.
   */
  loginModalOpen: boolean
  openLoginModal: () => void
  closeLoginModal: () => void
}

export const useSession = create<SessionState>((set) => ({
  sceneState: 'idle',
  setSceneState: (sceneState) => set({ sceneState }),
  modalZone: null,
  openModal: (modalZone) => set({ modalZone }),
  closeModal: () => set({ modalZone: null }),
  loginModalOpen: false,
  openLoginModal: () => set({ loginModalOpen: true }),
  closeLoginModal: () => set({ loginModalOpen: false }),
}))
